import { Injectable } from '@angular/core';
import * as Color from 'color';
import { templateUpdatePrimaryColorsPalettesDefault } from './color.functions';

@Injectable({
    providedIn: 'root',
})
export class ColorService {
    shadeRatio = 0.1;
    tintRatio = 0.1;

    constructor() {}

    /**
     * Actualiza los estilos primario, secundario y terciario y sus paletas, a partir de 3 valores hexadecimales
     * Esto va a servir para actualizar todos los demás colores que se basan n estos
     */
    updatePrimaryColorsPalettes(
        primary: string,
        secondary: string,
        tertiary: string,
        tertiaryContrast: string
    ) {
        templateUpdatePrimaryColorsPalettesDefault(
            {
                defaultTemplate: this.updatePrimaryColorsPalettesDefault,
                flowwwerTemplate: this.updatePrimaryColorsPalettesFlowwwer,
            },
            primary,
            secondary,
            tertiary,
            tertiaryContrast
        );
    }

    updatePrimaryColorsPalettesDefault = (
        primary: string,
        secondary: string,
        tertiary: string
    ) => {
        this.createColorPalette('ion-color-primary', primary);
        this.createColorPalette('ion-color-secondary', secondary);
        this.createColorPalette('ion-color-tertiary', tertiary);
    };

    /** En la plantilla de flowww, hemos cambiado la configuración de colores primarios, por lo que para no modificar los colores de los sistemas en bbdd
     * y que el cambio entre plantillas sea fácil, hemos cambiado los colores mediante esta función
     */
    updatePrimaryColorsPalettesFlowwwer = (
        primary: string,
        secondary: string,
        tertiary: string,
        tertiaryContrast: string
    ) => {
        this.createColorPalette('ion-color-primary', secondary);

        /** El secondary ya no llega del BE: el fondo se deriva del color que prima
         * (el de ion-color-primary) como custom-50 del DS = 10% del primary + 90% blanco,
         * y su contraste es el propio primary, para que el texto/checkmark vaya en rojo
         * primary sobre el fondo pálido (igual que los botones outline). */
        const primaryHex = this.checkHexadecimalColor(secondary);
        const secondaryHex = primaryHex
                ? this.getCustom50Color(primaryHex)
                : secondary;
        this.createColorPalette('ion-color-secondary', secondaryHex, {
            hexContrast: primaryHex,
        });

        this.createColorPalette('ion-color-tertiary', primary);
    };

    /**
     * Crea una paleta de colores completa para un color y la actualiza en la página
     */
    createColorPalette(
        key: string,
        hex: string,
        { hexContrast } = { hexContrast: null }
    ): void {
        if (key !== '' && hex !== '') {
            hex = this.checkHexadecimalColor(hex);
            try {
                const contrastColor = this.checkHexadecimalColor(hexContrast)
                        ? this.checkHexadecimalColor(hexContrast)
                        : this.getContrastColor(hex);
                const rgb = this.getRGBColor(hex).join(',');
                const contrastRgb = this.getRGBColor(contrastColor).join(',');
                const shade = this.getShadeColor(hex);
                const tint = this.getTintColor(hex);

                document.documentElement.style.setProperty(`--${key}`, hex);
                document.documentElement.style.setProperty(`--${key}-rgb`, rgb);
                document.documentElement.style.setProperty(`--${key}-contrast`, contrastColor);
                document.documentElement.style.setProperty(`--${key}-contrast-rgb`, contrastRgb);
                document.documentElement.style.setProperty(`--${key}-shade`, shade);
                document.documentElement.style.setProperty(`--${key}-tint`, tint);

                // Inject Design System Variables to match the Backend dynamically
                if (key === 'ion-color-primary') {
                    document.documentElement.style.setProperty(`--primary-color`, hex);
                    document.documentElement.style.setProperty(`--primary-color-rgb`, rgb);
                    document.documentElement.style.setProperty(`--primary-contrast-color`, contrastColor);
                    document.documentElement.style.setProperty(`--primary-color-shade`, shade);
                    document.documentElement.style.setProperty(`--primary-tint-color`, tint);

                    const softBg = this.getCustom50Color(hex);
                    document.documentElement.style.setProperty(`--color-action-primary`, hex);
                    document.documentElement.style.setProperty(`--color-action-primary-bg`, softBg);
                    document.documentElement.style.setProperty(`--color-action-primary-hover`, shade);

                    // El secondary ya no es un color independiente: usa el MISMO color que el primary
                    // (así el texto va en rojo primary, como los botones outline), con el custom-50
                    // como fondo suave. main=primary, bg=custom-50.
                    document.documentElement.style.setProperty(`--color-action-secondary`, hex);
                    document.documentElement.style.setProperty(`--color-action-secondary-bg`, softBg);
                }
            } catch (e) {
                console.log(e);
            }
        }
    }

    checkHexadecimalColor(hex) {
        if (hex?.charAt(0) !== '#') {
            hex = `#${hex}`;
        }
        if (!new RegExp('^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$', 'i').test(hex)) {
            return null;
        }
        return hex;
    }

    getRGBColor(hex: string): Array<number> {
        const hexVal = hex.substring(1, hex.length);
        const bigint = parseInt(hexVal, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return [r, g, b];
    }

    getContrastColor(color: string): string {
        const c = Color(color);
        return c.isDark() ? '#FFFFFF' : '#000000';
    }

    getShadeColor(hex: string): string {
        return Color(hex).darken(this.shadeRatio);
    }

    getTintColor(hex: string): string {
        return Color(hex).lighten(this.tintRatio);
    }

    /** custom-50 del DS como hex: 10% del color base + 90% blanco. */
    getCustom50Color(hex: string): string {
        return Color('#FFFFFF').mix(Color(hex), 0.1).hex();
    }
}
